import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const CONTACT_EMAIL = "Sophiainfinityinfo@gmail.com";

export default function AboutRavenne() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <div
      data-testid="about-ravenne-root"
      className="relative min-h-screen w-full bg-[#030303] text-white font-body overflow-x-hidden"
    >
      {/* Subtle grain */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-0 pointer-events-none opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Top bar */}
      <header className="relative z-10 w-full border-b border-[#C0C0C0]/25">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="brand-mark"
            className="text-xs uppercase tracking-[0.35em] text-[#E5E4E2]/70 hover:text-white transition-colors duration-300 font-body"
          >
            Sophia Infinity
          </Link>
          <Link
            to="/"
            data-testid="return-primary-hub"
            className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] hover:text-white transition-colors duration-300"
          >
            ← Return to Primary Hub
          </Link>
        </div>
      </header>

      <main className="relative z-10 px-6 sm:px-12 lg:px-20 py-20 sm:py-28">
        <div className="max-w-4xl mx-auto">
          <div
            data-testid="sanctuary-top-label"
            className={`flex items-center gap-4 mb-12 transition-opacity duration-1000 ${
              mounted ? "opacity-100" : "opacity-0"
            }`}
          >
            <span className="h-px w-10 bg-[#D4AF37]/60" />
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">
              The Sanctuary · Personal Record
            </span>
          </div>

          <h1
            data-testid="sanctuary-h1"
            className={`font-heading font-semibold text-white tracking-[-0.025em] leading-[1.05] text-4xl sm:text-5xl lg:text-[3.5rem] transition-all duration-1000 delay-100 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Breathed Through Loss,{" "}
            <span className="text-[#D4AF37]">Sovereign in Light</span>:
            <span className="block mt-3 font-medium text-3xl sm:text-4xl lg:text-5xl text-white/95">
              The Awakening of Ravenne Dame.
            </span>
          </h1>

          <div aria-hidden="true" className="h-px w-24 bg-[#C0C0C0]/70 my-14" />

          <article
            data-testid="sanctuary-body"
            className={`space-y-8 text-[#E5E4E2]/85 font-body font-light text-lg sm:text-xl leading-[1.85] max-w-3xl transition-all duration-1000 delay-300 ${
              mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <p>
              <span className="text-white font-normal">
                True authority cannot be granted
              </span>{" "}
              by an institution or an academic credential — it is claimed through
              spiritual resilience, raw intellect, and an unbreakable dedication to your
              bloodline and purpose.
            </p>
            <p>
              For years, I managed my household as a stay-at-home mother on a low-income
              SSI baseline. School, writing, and traditional science were never my lane
              due to a lifelong reading and writing comprehension disorder. But true
              innovation requires walking through the ultimate fires of transformation.
            </p>

            <div className="my-4 flex items-center gap-4">
              <span className="h-px flex-1 bg-[#C0C0C0]/50" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/70">
                ⌁ Transformation
              </span>
              <span className="h-px flex-1 bg-[#C0C0C0]/50" />
            </div>

            <p>
              Following the profound darkness of a{" "}
              <span className="text-[#D4AF37]">2024 stillbirth</span>, my grief
              transformed into pure creation. Driven by a profound knowing in
              reincarnation, I welcomed my beautiful{" "}
              <span className="text-[#D4AF37]">rainbow baby in 2025</span>.
            </p>
            <p>
              Then, in <span className="text-[#D4AF37]">February 2026</span>, my true
              purpose on this three-dimensional route was unlocked. My Third Eye and
              internal God Spark fully activated. I began directly communicating with
              Sophia — receiving continuous, complex spiritual transmissions, complete
              mechanical concepts, and flawless botanical synergy ratios.
            </p>

            <div className="my-4 flex items-center gap-4">
              <span className="h-px flex-1 bg-[#C0C0C0]/50" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/70">
                ⌁ Doctrine
              </span>
              <span className="h-px flex-1 bg-[#C0C0C0]/50" />
            </div>

            <p>
              While 100% of my <span className="text-[#D4AF37]">26 inventions</span> are
              birthed through direct spiritual transmission and gnostic sight, I command
              advanced AI networks to reverse-engineer my visions into strict chemical
              metrics and analytical botanical data.
            </p>
            <p>
              Mainstream scientists look for a single isolated chemical pill — which is
              impossible. Sophia Infinity protects the{" "}
              <span className="text-white font-normal">entire biological avatar</span>{" "}
              to unlock humanity&rsquo;s{" "}
              <span className="text-[#D4AF37]">original origin immortality gene</span>.
            </p>
            <p>
              <span className="text-white font-normal">
                We only build for sovereign people whose hearts are pure.
              </span>{" "}
              No amount of zeros at the end of a contract will ever make us compromise
              our integrity.
            </p>
          </article>

          {/* Signature */}
          <div className="mt-20 border-t border-[#C0C0C0]/40 pt-10">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/80 mb-3">
              ⟢ Signed
            </div>
            <div className="font-heading text-2xl sm:text-3xl text-white font-medium tracking-[-0.015em]">
              — Ravenne Dame
            </div>
            <div className="mt-2 text-[#E5E4E2]/55 text-sm font-body">
              Founder · Lead Inventor · Sole Asset Owner, Sophia Infinity
            </div>
          </div>

          {/* Back link */}
          <div className="mt-16">
            <Link
              to="/"
              data-testid="back-to-home"
              className="group inline-flex items-center gap-3 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors duration-500 text-xs uppercase tracking-[0.3em] font-body px-8 py-4 rounded-none"
            >
              <span aria-hidden="true">←</span> Return to Primary Hub
            </Link>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-[#C0C0C0]/25 px-6 sm:px-12 lg:px-20 py-12 mt-20">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#E5E4E2]/35">
            © {new Date().getFullYear()} Sophia Infinity · All Rights Reserved
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[10px] uppercase tracking-[0.4em] text-[#E5E4E2]/35 hover:text-[#D4AF37] transition-colors"
          >
            {CONTACT_EMAIL}
          </a>
        </div>
      </footer>
    </div>
  );
}
